import { Slot } from "@radix-ui/react-slot";
import * as React from "react";
import { cx } from "../internal/cx.js";

/**
 * MobileNavBar — the sticky bottom navigation bar for mobile (hidden on `lg`
 * and up, where a sidebar or top tab strip takes over). A floating pill of
 * icon + label items with a sliding active indicator, over a blur scrim.
 *
 * Router-agnostic (hard rule #6): items render via `asChild`, so the app passes
 * its own router `<Link>`/`<NavLink>`. App-specific overflow (a "More" sheet) is
 * just another item whose trigger the app owns — keep that logic in the app.
 *
 * ```tsx
 * <MobileNavBar aria-label="Main navigation">
 *   <MobileNavBar.Item asChild active={isBenefits}>
 *     <NavLink to="/benefits"><ShieldIcon /><span>Benefits</span></NavLink>
 *   </MobileNavBar.Item>
 *   <MobileNavBar.Item active={moreOpen} onClick={() => setMoreOpen(true)}>
 *     <MoreIcon /><span>More</span>
 *   </MobileNavBar.Item>
 * </MobileNavBar>
 * ```
 */

interface MobileNavBarContextValue {
  registerItem: (el: HTMLElement | null, active: boolean) => void;
}

const MobileNavBarContext = React.createContext<MobileNavBarContextValue | null>(null);

export interface MobileNavBarProps extends React.HTMLAttributes<HTMLElement> {
  /** Accessible name for the nav landmark. */
  "aria-label": string;
}

interface MobileNavBarComponent
  extends React.ForwardRefExoticComponent<MobileNavBarProps & React.RefAttributes<HTMLDivElement>> {
  Item: typeof MobileNavBarItem;
}

const MobileNavBarRoot = React.forwardRef<HTMLDivElement, MobileNavBarProps>(function MobileNavBar(
  { children, className, "aria-label": ariaLabel, ...props },
  ref,
) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const indicatorRef = React.useRef<HTMLDivElement>(null);
  const activeElRef = React.useRef<HTMLElement | null>(null);
  const initializedRef = React.useRef(false);

  // Items report their DOM node + active flag; the active one drives the
  // indicator's position/width. A version counter re-runs the effect on change.
  const [, forcePosition] = React.useReducer((n: number) => n + 1, 0);

  const registerItem = React.useCallback((el: HTMLElement | null, active: boolean) => {
    if (active) {
      activeElRef.current = el;
      forcePosition();
    } else if (activeElRef.current === el) {
      activeElRef.current = null;
      forcePosition();
    }
  }, []);

  React.useLayoutEffect(() => {
    const container = containerRef.current;
    const indicator = indicatorRef.current;
    const activeEl = activeElRef.current;
    if (!container || !indicator || !activeEl) return;

    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const place = () => {
      const c = container.getBoundingClientRect();
      const i = activeEl.getBoundingClientRect();
      const x = i.left - c.left;
      // First placement snaps (no animation); subsequent moves ease.
      if (!initializedRef.current || reduceMotion) {
        indicator.style.transition = "none";
        indicator.style.transform = `translateX(${x}px)`;
        indicator.style.width = `${i.width}px`;
        if (!initializedRef.current) {
          // Flush, then arm the transition for later moves.
          void indicator.offsetWidth;
          initializedRef.current = true;
          if (!reduceMotion) {
            indicator.style.transition = "transform 0.3s ease-in-out, width 0.3s ease-in-out";
          }
        }
        return;
      }
      indicator.style.transform = `translateX(${x}px)`;
      indicator.style.width = `${i.width}px`;
    };

    place();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  });

  const context = React.useMemo<MobileNavBarContextValue>(() => ({ registerItem }), [registerItem]);

  return (
    <div ref={ref} className={cx("cw-v2-mobile-nav", className)} {...props}>
      {/* Scrim: blurs page content beneath the floating bar. */}
      <div className="cw-v2-mobile-nav__scrim" aria-hidden="true" />
      <nav className="cw-v2-mobile-nav__bar" aria-label={ariaLabel}>
        <div ref={containerRef} className="cw-v2-mobile-nav__track">
          <div ref={indicatorRef} className="cw-v2-mobile-nav__indicator" aria-hidden="true" />
          <MobileNavBarContext.Provider value={context}>{children}</MobileNavBarContext.Provider>
        </div>
      </nav>
    </div>
  );
}) as MobileNavBarComponent;

export interface MobileNavBarItemProps extends React.HTMLAttributes<HTMLElement> {
  /** Marks the item as the current section — colours it and drives the indicator. */
  active?: boolean;
  /** Render onto the child element (a router `<Link>`) instead of a `<button>`. */
  asChild?: boolean;
}

const MobileNavBarItem = React.forwardRef<HTMLElement, MobileNavBarItemProps>(
  function MobileNavBarItem(
    { active = false, asChild = false, className, ...props },
    forwardedRef,
  ) {
    const ctx = React.useContext(MobileNavBarContext);
    const localRef = React.useRef<HTMLElement | null>(null);

    const setRef = React.useCallback(
      (el: HTMLElement | null) => {
        localRef.current = el;
        ctx?.registerItem(el, active);
        if (typeof forwardedRef === "function") forwardedRef(el);
        else if (forwardedRef) forwardedRef.current = el;
      },
      [ctx, active, forwardedRef],
    );

    // Re-register when active flips without a remount (route change).
    React.useEffect(() => {
      ctx?.registerItem(localRef.current, active);
    }, [ctx, active]);

    const Comp = asChild ? Slot : "button";
    const extraProps = asChild ? {} : { type: "button" as const };

    return (
      <Comp
        // biome-ignore lint/suspicious/noExplicitAny: Slot/button ref union is not expressible without a cast.
        ref={setRef as any}
        className={cx(
          "cw-v2-mobile-nav__link",
          active && "cw-v2-mobile-nav__link--active",
          className,
        )}
        aria-current={active ? "page" : undefined}
        {...extraProps}
        {...props}
      />
    );
  },
);
MobileNavBarItem.displayName = "MobileNavBar.Item";

MobileNavBarRoot.Item = MobileNavBarItem;

export const MobileNavBar = MobileNavBarRoot;
